import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, User, Phone, Wrench, Banknote } from 'lucide-react';
import type { Database } from '../lib/database.types';
import { formatPhoneNumber, formatDateTime } from '../lib/utils';

type Customer = Database['public']['Tables']['customers']['Row'];
type Repair = Database['public']['Tables']['repairs']['Row'] & {
  diagnostic: Database['public']['Tables']['diagnostics']['Row'][];
};

const CustomerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [repairs, setRepairs] = useState<Repair[]>([]);

  useEffect(() => {
    const fetchCustomerAndRepairs = async () => {
      if (!id) {
        setError('No customer ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch customer
        const { data: customerData, error: customerError } = await supabase
          .from('customers')
          .select('*')
          .eq('id', id)
          .single();

        if (customerError) throw customerError;
        if (!customerData) throw new Error('Customer not found');

        setCustomer(customerData);

        // Fetch repairs for this customer
        const { data: repairsData, error: repairsError } = await supabase
          .from('repairs')
          .select(`
            *,
            diagnostic:diagnostics(*)
          `)
          .eq('customer_id', id)
          .order('created_at', { ascending: false });

        if (repairsError) throw repairsError;
        setRepairs(repairsData || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load customer details');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerAndRepairs();
  }, [id]);

  const getLatestDiagnostic = (repair: Repair) => {
    if (!repair.diagnostic || repair.diagnostic.length === 0) return null;
    return repair.diagnostic.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse space-y-4 text-center">
          <div className="w-12 h-12 mx-auto">
            <User className="w-full h-full text-gray-300" />
          </div>
          <div className="text-gray-500">Loading customer details...</div>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error || 'Customer not found'}</div>
          <button
            onClick={() => navigate('/history')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 lg:pb-0 space-y-4 md:space-y-6">
      {/* Back button only - sticky */}
      <div className="sticky top-12 md:top-14 lg:top-14 z-10 bg-white border-b border-gray-200 -mt-2 md:-mt-3 lg:-mt-3 -mx-2 md:-mx-3 lg:-mx-4 px-2 md:px-3 lg:px-4 py-3 lg:border-0 lg:pb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium min-h-[44px]"
          aria-label={t('back')}
        >
          <ArrowLeft className="w-6 h-6" />
          {t('back')}
        </button>
      </div>

      {/* Customer Info - User icon scrolls with content */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <User className="w-6 h-6 text-blue-600" />
          Mijoz ma&apos;lumotlari
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <User className="w-4 h-4" />
              <span className="text-sm font-medium">Ism</span>
            </div>
            <a
              href={`/customer/${customer.id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/customer/${customer.id}`);
              }}
              className="text-2xl text-blue-600 hover:text-blue-700 hover:underline transition-colors font-medium"
            >
              {customer.name}
            </a>
          </div>
          <div>
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">Telefon</span>
            </div>
            <a 
              href={`tel:${customer.phone}`}
              className="text-2xl text-gray-900 font-medium hover:text-blue-600 hover:underline transition-colors"
            >
              {formatPhoneNumber(customer.phone)}
            </a>
          </div>
        </div>
      </div>

      {/* Repairs List */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Ta'mirlashlar ({repairs.length})
          </h2>
        </div>
        <div className="p-6">
          {repairs.length > 0 ? (
            <div className="space-y-4">
              {repairs.map((repair) => {
                const diagnostic = getLatestDiagnostic(repair);
                return (
                  <div
                    key={repair.id}
                    className="w-full border rounded-lg p-4 space-y-4"
                  >
                    <button
                    onClick={() => navigate(`/repair/${repair.repair_id}`)}
                      className="w-full text-left hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="font-mono text-2xl text-blue-600">ID: {repair.repair_id}</div>
                      <div className="text-2xl text-gray-500">{formatDateTime(repair.created_at)}</div>
                    </div>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <Wrench className="w-5 h-5 text-gray-400" />
                          <span className="text-base capitalize">
                            {repair.device_type}
                            {repair.device_model && (
                              <span className="font-bold text-blue-600 text-lg">
                                {` - ${repair.device_model}`}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm">
                        <span className={`px-3 py-1 rounded-full ${
                          repair.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : repair.status === 'diagnosed'
                            ? 'bg-blue-100 text-blue-800'
                            : repair.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {t(repair.status)}
                        </span>
                      </div>
                    </div>

                    <div className="text-sm text-gray-600">
                      <strong>{t('issue')}:</strong> {repair.issue_description}
                    </div>

                    {diagnostic && (
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500 mb-1">{t('diagnosticNotes')}</div>
                          <div>{diagnostic.notes}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 mb-1">{t('requiredParts')}</div>
                          <div>{diagnostic.required_parts || t('none')}</div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-end pt-4 border-t">
                      {diagnostic && diagnostic.estimated_cost && (
                        <div className="flex items-center gap-1 text-blue-600">
                          <Banknote className="w-4 h-4" />
                          <span>{t('estimatedCost')}: {diagnostic.estimated_cost.toLocaleString()} UZS</span>
                        </div>
                      )}
                    </div>
                  </button>
                    <button
                      onClick={() => navigate(`/history/repair/${repair.id}`)}
                      className="mt-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Ta'mirlashni ochish
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              Bu mijoz uchun ta'mirlashlar topilmadi
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;

